import type { InspectionRecordAttachment } from '@/services/inspections/types';
import { s3Service } from '@/services/s3';
import { extractOriginalName } from '@/shared/utils/extract-original-name.util';
import { downloadFromSignedUrl } from '@/shared/utils/signed-url.util';
import {
  Button,
  Dialog,
  Timeline,
} from '@noorden-insurtech/alba-design-system';
import { Download, FileText, Folder, Loader2, UserRound } from 'lucide-react';
import { useState } from 'react';
import { useRecordsInspection } from '../../hooks/use-records-inspection.hook';

function mapCreatedAt(createdAt: string) {
  const date = new Date(createdAt);

  return {
    day: date.getDate(),
    month: date.toLocaleString('pt-BR', { month: 'short' }),
    time: date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
}

function normalizeStatus(status: string | undefined | null): string {
  return status?.trim().toLowerCase() ?? '';
}

interface InspectionInteractionsTimelineProps {
  policyId: string;
  inspectionId: string;
  enabled?: boolean;
  showHeading?: boolean;
}

export function InspectionInteractionsTimeline({
  policyId,
  inspectionId,
  enabled = true,
  showHeading = true,
}: InspectionInteractionsTimelineProps) {
  const { data: records } = useRecordsInspection(policyId, inspectionId, {
    enabled,
  });
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function handleDownloadAttachment(
    attachment: InspectionRecordAttachment
  ) {
    try {
      setDownloadingId(attachment.id);
      const signedUrl = await s3Service.fetchPresignedDownloadUrl(
        attachment.keyPath
      );
      await downloadFromSignedUrl(
        signedUrl,
        extractOriginalName(attachment.keyPath)
      );
    } catch (error) {
      console.error('Erro ao baixar anexo:', error);
    } finally {
      setDownloadingId(null);
    }
  }

  const items = records?.map(record => {
    const createdAt = mapCreatedAt(record.createdAt);
    return {
      id: record.id,
      day: createdAt.day,
      month: createdAt.month,
      time: createdAt.time,
      title: mapAction[record.action] ?? record.action,
      status: record.status,
      observations: record.description,
      technicalOpinion: record.technicalOpinion,
      attachments: record.attachments ?? [],
      author: record.userName || '-',
    };
  });

  return (
    <Timeline.Root>
      {showHeading && (
        <Timeline.Heading className='text-alba-black-500 text-base'>
          Registro de Interações
        </Timeline.Heading>
      )}

      {items && items.length > 0 && (
        <Timeline.List>
          {items?.map(entry => (
            <Timeline.Item key={entry.id}>
              <Timeline.Date
                day={entry.day}
                month={entry.month}
                time={entry.time}
              />
              <Timeline.Marker />

              <Timeline.Card size='full'>
                <Timeline.CardTitle>{entry.title}</Timeline.CardTitle>

                <Timeline.CardDivider />

                <Timeline.CardBody>
                  {entry.status && (
                    <p className='font-montserrat text-sm leading-[150%] text-alba-gray-600'>
                      <span className='font-medium'>Status:</span>{' '}
                      {mapStatus[normalizeStatus(entry.status)] ?? entry.status}
                    </p>
                  )}

                  {entry.observations && (
                    <p className='font-montserrat text-sm leading-[150%] text-alba-gray-600'>
                      <span className='font-medium'>Observações:</span>{' '}
                      {entry.observations}
                    </p>
                  )}

                  {entry.technicalOpinion && (
                    <p className='font-montserrat text-sm leading-[150%] text-alba-gray-600'>
                      <span className='font-medium'>Parecer técnico:</span>{' '}
                      {entry.technicalOpinion}
                    </p>
                  )}

                  {entry.attachments.length > 0 && (
                    <div className='pt-1'>
                      <Dialog.Root>
                        <Dialog.Trigger asChild>
                          <Button variant='secondary' size='md'>
                            Visualizar arquivos anexados (
                            {entry.attachments.length})
                          </Button>
                        </Dialog.Trigger>
                        <Dialog.Portal>
                          <Dialog.Backdrop />
                          <Dialog.Positioner>
                            <Dialog.Content className='w-full max-w-md pb-2'>
                              <Dialog.Header className='flex flex-row items-center justify-between'>
                                <Dialog.Title className='text-lg'>
                                  <Folder className='text-alba-gray-600 size-6' />
                                  Arquivos anexados
                                </Dialog.Title>
                                <Dialog.CloseTrigger asChild>
                                  <Dialog.CloseButton />
                                </Dialog.CloseTrigger>
                              </Dialog.Header>

                              <Dialog.Body className='flex flex-col gap-4'>
                                {entry.attachments.map(attachment => (
                                  <button
                                    key={attachment.id}
                                    type='button'
                                    onClick={() =>
                                      handleDownloadAttachment(attachment)
                                    }
                                    disabled={downloadingId === attachment.id}
                                    className='flex flex-row items-center justify-start gap-2 px-3 py-2 border border-alba-gray-400 rounded-lg hover:bg-alba-gray-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                                  >
                                    <FileText className='text-alba-black-500 size-6 shrink-0' />

                                    <div className='flex-1 flex flex-col items-start justify-start'>
                                      <p className='font-montserrat text-sm text-alba-black-500'>
                                        {extractOriginalName(attachment.keyPath)}
                                      </p>
                                      <p className='font-montserrat text-xs font-normal text-alba-gray-600'>
                                        {attachment.documentType}
                                      </p>
                                    </div>

                                    {downloadingId === attachment.id ? (
                                      <Loader2 className='text-alba-blue-600 size-4 animate-spin' />
                                    ) : (
                                      <Download className='text-alba-blue-600 size-4' />
                                    )}
                                  </button>
                                ))}
                              </Dialog.Body>
                            </Dialog.Content>
                          </Dialog.Positioner>
                        </Dialog.Portal>
                      </Dialog.Root>
                    </div>
                  )}
                </Timeline.CardBody>

                <Timeline.CardFooter>
                  <UserRound size={16} className='text-alba-gray-600' />
                  <span className='font-montserrat text-sm leading-[150%] text-alba-gray-600'>
                    Alterado por {entry.author}
                  </span>
                </Timeline.CardFooter>
              </Timeline.Card>
            </Timeline.Item>
          ))}
        </Timeline.List>
      )}

      {items?.length === 0 && (
        <div className='flex flex-col items-center justify-center'>
          <p className='font-montserrat text-sm text-alba-gray-600'>
            Nenhuma interação encontrada para esta inspeção.
          </p>
        </div>
      )}
    </Timeline.Root>
  );
}

const mapAction: Record<string, string> = {
  CREATE: 'Inspeção Criada',
  UPDATE: 'Inspeção Atualizada',
  REJECT: 'Proposta Recusada pela Inspeção',
  ACCEPT: 'Proposta Aceita pela Inspeção',
  'Parecer Técnico Salvo': 'Parecer Técnico Salvo',
  'Status Alterado': 'Status Alterado',
  'Proposta Recusada pela Inspeção': 'Proposta Recusada pela Inspeção',
  'Proposta Aprovada pela Inspeção': 'Proposta Aprovada pela Inspeção',
};

const mapStatus: Record<string, string> = {
  requested: 'Solicitada',
  in_progress: 'Em andamento',
  frustrated: 'Frustrado',
  frustated: 'Frustrado',
  canceled: 'Cancelado',
  under_review: 'Em análise',
  dismissed: 'Dispensar Inspeção',
  pending: 'Pendente',
  peding: 'Pendente',
  accepted: 'Aceito',
};
